import { Link } from "react-router-dom";
import {
  ArrowRight, Search, Megaphone, Target, BadgeCheck, Shield, RefreshCw, Wallet,
  Check, X, MessageSquare, Cpu, UserCheck, Ban, Layers, FileText, BellRing
} from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import Seo, { breadcrumbJsonLd, serviceJsonLd } from "@/components/Seo";

const PIPELINE_IMG = "/images/metodo-hero-1.png";
const CARD_IMG = "/images/metodo-hero-2.png";

const CHANNELS = [
  {
    n: "01",
    icon: Search,
    badge: "Hunting diretto",
    title: "10 milioni di figure professionali verificate",
    intro: "Il nostro canale principale.",
    body: [
      "Accediamo a un database di oltre 10 milioni di profili professionali verificati. La tecnologia identifica i profili più coerenti con la ricerca, per ruolo, settore, seniority, area geografica.",
      "I recruiter Lovac li contattano direttamente, con messaggi personalizzati. Un contatto umano, su una persona già valutata prima di essere raggiunta.",
      "Questo è il canale dove troviamo chi non stava cercando lavoro. Spesso le persone migliori.",
    ],
  },
  {
    n: "02",
    icon: Megaphone,
    badge: "Advertising",
    title: "Campagne mirate su Facebook, Instagram e TikTok",
    intro: null,
    body: [
      "Per alcune ricerche affianchiamo campagne advertising sui social, contenuti statici e video pensati per intercettare profili specifici e portarli sul nostro processo di selezione.",
      "Non raccogliamo CV. Chi risponde entra in un percorso strutturato che filtra dalla massa i profili che vale davvero la pena presentarti.",
    ],
  },
  {
    n: "03",
    icon: Target,
    badge: "Annunci mirati",
    title: "LinkedIn e portali, usati in modo diverso",
    intro: "Li usiamo, in modo selettivo, per alcune posizioni.",
    body: [
      "La differenza non è il canale, è quello che succede dopo. Chi risponde non ci manda un CV che finisce in una cartella. Entra nel nostro processo di selezione: screening, colloquio con il recruiter, valutazione prima di arrivare da te.",
    ],
  },
];

const AI_BLOCKS = [
  {
    n: "01",
    icon: MessageSquare,
    title: "Apertura della ricerca",
    lead: "Tutto parte da una conversazione.",
    body:
      "Quando apri una ricerca con Lovac, il recruiter dedicato raccoglie un brief approfondito: il ruolo, il team, la cultura aziendale, le dinamiche specifiche del tuo contesto. Non un form da compilare, un confronto vero, per capire davvero chi cerchi. Più conosciamo la tua realtà, più la ricerca è precisa fin dal primo giorno.",
    image: "/images/metodo-box1-apertura.png",
    imageAlt: "Brief approfondito tra cliente e recruiter Lovac con analisi dati",
  },
  {
    n: "02",
    icon: Cpu,
    title: "Identificazione dei profili",
    lead: "L'AI confronta. Il recruiter valuta.",
    body:
      "Sul database di 10 milioni di profili, l'AI confronta ogni candidato con il profilo che stiamo cercando, non per parole chiave, ma per coerenza reale tra traiettoria professionale e ruolo aperto. Produce una valutazione ragionata: perché quel profilo è interessante, dove ci sono punti di attenzione, cosa vale la pena approfondire. Il recruiter usa quella valutazione come punto di partenza, non come verdetto finale.",
    image: "/images/metodo-box2-identificazione.png",
    imageAlt: "Lista candidati con matching AI e selezione profilo",
  },
  {
    n: "03",
    icon: UserCheck,
    title: "Colloquio e valutazione",
    lead: "Ogni candidato incontra un recruiter Lovac.",
    body:
      "Non è un pre-screening veloce. È un colloquio vero: valutiamo se la figura risponde al profilo che cerchi, se ha l'attitudine giusta per lavorare nel tuo contesto, se la motivazione è reale. Solo chi supera questa fase entra nella rosa dei candidati da presentarti.\n\nPer figure tecniche, manageriali o altamente specializzate, manager, profili sales, tech, finance, il percorso può prevedere ulteriori colloqui o prove pratiche con specialisti di settore, per verificare le competenze sul campo e non solo sulla carta.",
    image: "/images/metodo-box3-colloquio.png",
    imageAlt: "Videocolloquio tra candidato e recruiter Lovac",
  },
];

const AI_NOT = [
  "Nessun sistema che esclude candidati automaticamente senza supervisione umana",
  "Nessuna analisi delle emozioni nei video colloqui",
  "Nessuna valutazione dei profili social",
  "Nessuna decisione autonoma su chi presentare o scartare",
  "Nessun utilizzo dei dati dei candidati per addestrare modelli AI senza consenso",
];

const COMMERCIAL = [
  {
    icon: BadgeCheck,
    title: "Success fee",
    body: "Una percentuale sulla RAL della figura inserita. Non paghi nulla finché non hai il candidato in azienda.",
  },
  {
    icon: Shield,
    title: "Garanzia sul periodo di prova",
    body: "Copertura sul superamento del periodo di prova della figura inserita. Il rischio è nostro, non tuo.",
  },
  {
    icon: RefreshCw,
    title: "Rimborso totale dell'acconto",
    body: "Se non ti presentiamo candidati coerenti con il brief, l'acconto viene rimborsato interamente. Non è una clausola nascosta, è il modo in cui lavoriamo.",
  },
  {
    icon: Wallet,
    title: "Spese operative a carico di Lovac",
    body: "Il budget pubblicitario e le spese operative della ricerca sono a nostro carico. Tu paghi solo il risultato.",
  },
];

const COMPARE_ROWS = [
  { k: "Metodo di ricerca", old: "Annunci + CV in entrata", neu: "Hunting su 10M profili, advertising, annunci mirati sul processo" },
  { k: "Chi trovano", old: "Chi sta cercando lavoro", neu: "Chi vale la pena cercare, anche chi non cerca" },
  { k: "Selezione", old: "La fa il cliente", neu: "La fa il recruiter Lovac prima che tu incontri chiunque" },
  { k: "Trasparenza", old: "Nessuna visibilità", neu: "Dashboard in tempo reale, aggiornamenti costanti" },
  { k: "Velocità", old: "Settimane o mesi", neu: "Primi candidati entro 15 giorni" },
  { k: "Rischio economico", old: "Paghi comunque", neu: "Rimborso se non portiamo candidati in target" },
  { k: "Tecnologia", old: "CRM base", neu: "AI proprietaria, database 10M profili, matching semantico" },
  { k: "Normativa AI", old: "Non dichiarata", neu: "Conforme all'AI Act europeo, ogni decisione è umana" },
];

export default function Metodo() {
  return (
    <>
      <Seo
        title="Il metodo Lovac, Hunting attivo + AI + recruiter dedicati | Lovac"
        description="Il metodo Lovac per trovare manager, tecnici specializzati e profili senior: hunting attivo su 10M+ profili, AI per screening, recruiter dedicati e portale in tempo reale."
        path="/metodo"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            serviceJsonLd({
              name: "Head hunting tech-driven",
              description:
                "Hunting attivo su 10M+ profili, AI per screening, recruiter dedicati e portale in tempo reale per PMI italiane.",
              path: "/metodo",
            }),
            breadcrumbJsonLd([
              { name: "Home", path: "/" },
              { name: "Metodo", path: "/metodo" },
            ]),
          ],
        }}
      />
      {/* 1. HERO */}
      <section className="bg-lovac-dark text-on-dark grain relative overflow-hidden">
        <div className="container-lovac px-6 pt-24 pb-20 relative z-10">
          <span className="badge-on-dark">Il nostro metodo</span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-6 text-on-dark max-w-4xl leading-[1.05]">
            Gestire un team è già abbastanza difficile.<br />
            <span className="text-lovac-accent">A trovare le persone giuste ci pensiamo noi.</span>
          </h1>
          <p className="text-on-dark-muted text-lg mt-6">Ecco come lo facciamo.</p>
        </div>
      </section>

      {/* 2. IL PROBLEMA */}
      <section className="section bg-lovac">
        <div className="container-lovac grid lg:grid-cols-[1fr_1.4fr] gap-14 items-start">
          <div className="lg:sticky lg:top-28">
            <p className="badge-lovac">Il problema</p>
            <h2 className="font-display text-4xl sm:text-5xl mt-4 text-lovac-dark leading-tight">
              Le agenzie tradizionali hanno un problema strutturale.
            </h2>
          </div>
          <div className="space-y-6 text-lg text-lovac-dark leading-relaxed max-w-2xl">
            <p className="font-medium">Non è la buona volontà. È il metodo.</p>
            <p>
              Pubblicano un annuncio sugli stessi portali che useresti tu. Raccolgono CV da chi sta cercando lavoro in quel momento, spesso non i profili migliori. Ti mandano una lista. Tu fai i colloqui, scrematura inclusa.
            </p>
            <p className="border-l-2 border-lovac-accent pl-5 italic text-lovac-dark">
              Hai pagato per un servizio che avresti potuto fare da solo.
            </p>
            <p>
              Il problema vero: i profili senior che ti servono non stanno mandando CV. Sono già occupati, stanno bene dove sono, e non hanno nessuna intenzione di rispondere a un annuncio su un portale.
            </p>
            <p className="font-display text-2xl text-lovac-dark mt-2">
              Per trovarli bisogna andarli a cercare.
            </p>
          </div>
        </div>
      </section>

      {/* 3. I TRE CANALI */}
      <section className="section bg-lovac-2">
        <div className="container-lovac">
          <div className="max-w-3xl">
            <p className="badge-lovac">I canali</p>
            <h2 className="font-display text-4xl sm:text-5xl mt-4 text-lovac-dark">
              Come troviamo le persone giuste.
            </h2>
            <p className="text-lovac-muted mt-5 text-base max-w-2xl">
              Non aspettiamo candidature. Usiamo tre canali in parallelo, e in ognuno il processo a valle è sempre lo stesso: selezione reale, prima che tu incontri chiunque.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-5 mt-14">
            {CHANNELS.map((c) => {
              const Icon = c.icon;
              return (
                <div key={c.n} className="card-lovac h-full flex flex-col">
                  <div className="flex items-center justify-between mb-6">
                    <div className="h-11 w-11 rounded-lg bg-lovac-2 flex items-center justify-center border-lovac">
                      <Icon size={18} className="text-lovac-dark" />
                    </div>
                    <span className="font-mono text-xs text-lovac-muted">{c.n}</span>
                  </div>
                  <span className="badge-lovac w-fit">{c.badge}</span>
                  <h3 className="font-display text-xl text-lovac-dark mt-4 leading-tight">{c.title}</h3>
                  <div className="mt-4 space-y-3 text-sm text-lovac-muted leading-relaxed">
                    {c.intro && <p className="font-medium text-lovac-dark">{c.intro}</p>}
                    {c.body.map((p, i) => <p key={i}>{p}</p>)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 4. L'AI NEL PROCESSO */}
      <section className="section bg-lovac">
        <div className="container-lovac">
          <div className="max-w-3xl">
            <p className="badge-lovac">L'AI nel processo</p>
            <h2 className="font-display text-4xl sm:text-5xl mt-4 text-lovac-dark">
              L'AI accelera. <span className="text-lovac-muted">I recruiter decidono.</span>
            </h2>
            <p className="text-lovac-muted mt-5 text-base">
              La tecnologia è al centro del metodo, non come marketing, come operatività reale. Ma con una regola precisa.
            </p>
            <div className="card-lovac mt-8">
              <p className="text-base text-lovac-dark leading-relaxed">
                Lovac usa l'AI per accelerare il lavoro dei recruiter, analisi dei profili, schede candidato, report di mercato. Ma con una regola precisa: <strong>ogni decisione su un candidato, chi contattare, chi presentare, chi raccomandare, è sempre presa da un professionista Lovac.</strong>
              </p>
              <p className="text-base text-lovac-muted mt-4 leading-relaxed">
                L'AI ci permette di farlo meglio e più velocemente. Non di farlo al posto nostro.
              </p>
            </div>
          </div>

          {/* 3 step blocks */}
          <div className="mt-16 space-y-12">
            {AI_BLOCKS.map((b, i) => {
              const Icon = b.icon;
              const reverse = i % 2 === 1;
              return (
                <div key={b.n} className={`grid lg:grid-cols-2 gap-10 items-center ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}>
                  <div>
                    <p className="font-mono text-lovac-accent text-sm">{b.n} / 03</p>
                    <h3 className="font-display text-3xl mt-3 text-lovac-dark leading-tight">{b.title}</h3>
                    <p className="text-lovac-dark mt-4 font-medium">{b.lead}</p>
                    <div className="text-lovac-muted mt-3 text-base leading-relaxed whitespace-pre-line">
                      {b.body}
                    </div>
                  </div>
                  <div className="relative">
                    {b.image ? (
                      <div className="flex items-center justify-center">
                        <img
                          src={b.image}
                          alt={b.imageAlt}
                          className="w-full h-auto max-w-[700px] mx-auto"
                          style={{ transform: "scale(1.2)", transformOrigin: "center" }}
                          loading="lazy"
                        />
                      </div>
                    ) : (
                      <div className="card-dark min-h-[260px] flex flex-col justify-between">
                        <div className="flex items-center justify-between">
                          <div className="h-12 w-12 rounded-lg bg-[rgba(0,196,140,0.15)] flex items-center justify-center">
                            <Icon size={20} className="text-lovac-accent" />
                          </div>
                          <span className="font-mono text-xs text-on-dark-muted">{b.n} / 03</span>
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-wider text-on-dark-muted">Fase {b.n}</p>
                          <p className="text-on-dark font-display text-2xl mt-2">{b.title}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

          </div>

        </div>
      </section>

      {/* 4b. COSA Lovac NON FA, sezione dedicata, centrata, premium */}
      <section
        className="relative overflow-hidden bg-lovac-dark text-on-dark grain"
        data-testid="metodo-cosa-non-fa"
      >
        {/* accent glow */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(60% 50% at 50% 0%, rgba(0,196,140,0.10) 0%, rgba(0,196,140,0) 60%)",
          }}
        />
        <div className="container-lovac px-6 py-24 lg:py-28 relative z-10">
          {/* Header centrato */}
          <div className="max-w-3xl mx-auto text-center">
            <span className="badge-on-dark inline-flex">Limiti che ci siamo dati</span>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-6 text-on-dark leading-[1.05]">
              Cosa Lovac <span className="text-lovac-accent">non fa.</span>
            </h2>
            <p className="text-on-dark-muted mt-6 text-base sm:text-lg leading-relaxed">
              Trasparenza vuol dire anche essere chiari su cosa non facciamo,
              per principio, per compliance, per rispetto delle persone.
            </p>
          </div>

          {/* Lista centrata, 5 item su griglia 2 colonne con ultimo full-width */}
          <ul className="mt-16 max-w-5xl mx-auto grid md:grid-cols-2 gap-4 sm:gap-5">
            {AI_NOT.map((t, i) => {
              const isLast = i === AI_NOT.length - 1;
              return (
                <li
                  key={t}
                  className={`group relative rounded-2xl p-6 sm:p-7 flex items-start gap-5 transition-all duration-300 hover:-translate-y-0.5 ${
                    isLast ? "md:col-span-2" : ""
                  }`}
                  style={{
                    background: "rgba(255,255,255,0.035)",
                    border: "0.5px solid rgba(255,255,255,0.10)",
                    backdropFilter: "blur(8px)",
                  }}
                >
                  {/* numero discreto */}
                  <span className="absolute top-4 right-5 font-mono text-[10px] uppercase tracking-wider text-on-dark-muted">
                    0{i + 1} / 0{AI_NOT.length}
                  </span>
                  <div
                    className="h-11 w-11 rounded-xl flex items-center justify-center shrink-0 transition-transform duration-300 group-hover:scale-105"
                    style={{
                      background: "rgba(0,196,140,0.14)",
                      border: "0.5px solid rgba(0,196,140,0.25)",
                    }}
                  >
                    <Ban size={18} className="text-lovac-accent" />
                  </div>
                  <p className="text-base sm:text-[17px] text-on-dark leading-relaxed pr-10">
                    {t}
                  </p>
                </li>
              );
            })}
          </ul>

          {/* Closing line */}
          <p className="mt-14 text-center text-sm text-on-dark-muted max-w-2xl mx-auto leading-relaxed">
            Conforme all'<span className="text-on-dark font-medium">AI Act europeo</span>,
            ogni decisione che riguarda una persona è presa da una persona.
          </p>
        </div>
      </section>

      {/* 5. IL PORTALE */}
      <section className="section bg-lovac-2">
        <div className="container-lovac">
          <div className="max-w-3xl">
            <p className="badge-lovac">Il portale cliente</p>
            <h2 className="font-display text-4xl sm:text-5xl mt-4 text-lovac-dark leading-tight">
              Non aspetti aggiornamenti.<br />
              <span className="text-lovac-muted">Li vedi in tempo reale.</span>
            </h2>
            <p className="text-lovac-muted mt-5 text-base">
              Il portale Lovac è lo strumento che condividiamo con ogni cliente durante una ricerca attiva. Niente email di stato, niente "ci sentiamo la prossima settimana". Apri il portale e sai esattamente cosa sta succedendo.
            </p>
          </div>

          {/* Pipeline */}
          <div className="mt-16 grid lg:grid-cols-[1.1fr_1fr] gap-10 items-center">
            <div className="relative">
              <img
                src={PIPELINE_IMG}
                alt="Dashboard pipeline candidati Lovac"
                className="rounded-xl border-lovac shadow-[0_8px_40px_rgba(0,0,0,0.08)] w-full"
              />
            </div>
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Layers size={18} className="text-lovac-accent" />
                <span className="text-[11px] uppercase tracking-wider text-lovac-muted">Sottosezione 1, Pipeline</span>
              </div>
              <h3 className="font-display text-3xl text-lovac-dark leading-tight">
                Da 128 contatti a 1 inserimento, tutto in un colpo d'occhio.
              </h3>
              <p className="text-lovac-muted mt-5 leading-relaxed">
                La prima schermata mostra il funnel completo della ricerca. Ogni numero racconta una fase del lavoro che Lovac sta facendo per te.
              </p>
              <ul className="mt-6 space-y-3 text-sm">
                {[
                  ["Contattati", "Quante figure abbiamo raggiunto direttamente, anche chi non stava cercando lavoro"],
                  ["Colloqui fissati", "Chi ha risposto e ha accettato di parlare con noi"],
                  ["Colloqui svolti", "Chi abbiamo già intervistato e valutato"],
                  ["Da presentare", "I profili che il recruiter ha selezionato e sta per portarti"],
                  ["Presentati", "Chi hai già incontrato o stai incontrando"],
                  ["Inserito", "Il risultato"],
                ].map(([k, v]) => (
                  <li key={k} className="flex items-start gap-3 pb-3 border-b border-lovac last:border-0">
                    <span className="font-medium text-lovac-dark w-32 shrink-0">{k}</span>
                    <span className="text-lovac-muted">{v}</span>
                  </li>
                ))}
              </ul>
              <p className="text-sm text-lovac-muted mt-6 italic">
                I nuovi profili hanno un badge verde, sai sempre cosa è cambiato dall'ultima volta che hai aperto il portale.
              </p>
            </div>
          </div>

          {/* Scheda candidato */}
          <div className="mt-20 grid lg:grid-cols-[1fr_1.1fr] gap-10 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <FileText size={18} className="text-lovac-accent" />
                <span className="text-[11px] uppercase tracking-wider text-lovac-muted">Sottosezione 2, Scheda candidato</span>
              </div>
              <h3 className="font-display text-3xl text-lovac-dark leading-tight">
                Non un CV da leggere. <span className="text-lovac-muted">Un briefing.</span>
              </h3>
              <p className="text-lovac-muted mt-5 leading-relaxed">
                Clicchi su un candidato e si apre la scheda.
              </p>
              <div className="mt-6 space-y-5">
                <div className="card-lovac">
                  <p className="text-[11px] uppercase tracking-wider text-lovac-accent">Sintesi AI</p>
                  <p className="text-sm text-lovac-dark mt-2 leading-relaxed">
                    Un paragrafo che racconta il profilo in modo narrativo: traiettoria, punti di forza, motivazione al cambio, allineamento con quello che stai cercando. Generata dall'AI sulla base del colloquio con il recruiter Lovac, non da un algoritmo che legge parole chiave.
                  </p>
                </div>
                <div className="card-lovac">
                  <p className="text-[11px] uppercase tracking-wider text-lovac-accent">Consigli per il colloquio</p>
                  <p className="text-sm text-lovac-dark mt-2 leading-relaxed">
                    Tre domande o aree da approfondire, specifiche per quel candidato. Arrivi preparato. Sai già cosa esplorare e perché.
                  </p>
                </div>
                <div className="card-lovac">
                  <p className="text-[11px] uppercase tracking-wider text-lovac-accent">Esperienze chiave</p>
                  <p className="text-sm text-lovac-dark mt-2 leading-relaxed">
                    Lo storico professionale sintetizzato. Solo quello che è rilevante per la ricerca in corso.
                  </p>
                </div>
              </div>
            </div>
            <div className="lg:order-first">
              <img
                src={CARD_IMG}
                alt="Scheda candidato Lovac con sintesi AI"
                className="rounded-xl border-lovac shadow-[0_8px_40px_rgba(0,0,0,0.08)] w-full"
              />
            </div>
          </div>

          {/* Referente */}
          <div className="mt-20 card-dark grid lg:grid-cols-[auto_1fr] gap-10 lg:gap-12 items-start p-8 sm:p-10 lg:p-14">
            <div className="h-16 w-16 rounded-xl bg-[rgba(0,196,140,0.15)] flex items-center justify-center shrink-0">
              <BellRing size={26} className="text-lovac-accent" />
            </div>
            <div className="max-w-3xl">
              <p className="text-[11px] uppercase tracking-wider text-on-dark-muted">Il portale è vivo</p>
              <h3 className="font-display text-2xl sm:text-3xl lg:text-4xl text-on-dark mt-3 leading-tight">
                Tecnologia trasparente. <span className="text-on-dark-muted">Persona reale dietro.</span>
              </h3>
              <div className="text-on-dark-muted mt-7 space-y-5 leading-relaxed">
                <p>
                  Ogni volta che un candidato avanza, la dashboard si aggiorna. Ogni volta che il recruiter completa un colloquio, la scheda si arricchisce. Non devi fare niente. Apri, leggi, decidi.
                </p>
                <p>
                  Ma la tecnologia è solo una parte. Ogni ricerca ha un referente Lovac dedicato, una persona reale, sempre raggiungibile, con cui confrontarti in ogni momento del processo. Non rispondi a un centralino. Hai un numero, un nome, e qualcuno che conosce la tua ricerca nel dettaglio.
                </p>
                <p className="text-on-dark">
                  Ogni cliente ha il proprio spazio isolato, vedi solo la tua ricerca, i tuoi candidati, i tuoi dati.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6. MODELLO COMMERCIALE */}
      <section className="section bg-lovac">
        <div className="container-lovac">
          <div className="max-w-3xl">
            <p className="badge-lovac">Il modello commerciale</p>
            <h2 className="font-display text-4xl sm:text-5xl mt-4 text-lovac-dark">
              Paghi quando hai il risultato.
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
            {COMMERCIAL.map((c, i) => {
              const Icon = c.icon;
              return (
                <div key={c.title} className="card-lovac h-full">
                  <div className="flex items-center justify-between mb-5">
                    <div className="h-10 w-10 rounded-lg bg-lovac-2 flex items-center justify-center border-lovac">
                      <Icon size={18} className="text-lovac-accent" />
                    </div>
                    <span className="font-mono text-xs text-lovac-muted">0{i + 1}</span>
                  </div>
                  <h3 className="font-display text-xl text-lovac-dark leading-tight">{c.title}</h3>
                  <p className="text-sm text-lovac-muted mt-3 leading-relaxed">{c.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 7. TABELLA CONFRONTO ESTESA */}
      <section className="section bg-lovac-2">
        <div className="container-lovac">
          <div className="max-w-3xl">
            <p className="badge-lovac">Il confronto, in dettaglio</p>
            <h2 className="font-display text-4xl sm:text-5xl mt-4 text-lovac-dark">
              Un'agenzia di head hunting moderna,<br />
              <span className="text-lovac-muted">in un settore vecchio.</span>
            </h2>
          </div>

          <div className="mt-12 card-lovac overflow-x-auto p-0">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-lovac">
                  <TableHead className="w-[180px] text-[11px] uppercase tracking-wider text-lovac-muted px-6 py-5">Criterio</TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wider text-lovac-muted px-6 py-5">Agenzie tradizionali</TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wider text-lovac-accent px-6 py-5">Lovac</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {COMPARE_ROWS.map((r) => (
                  <TableRow key={r.k} className="border-b border-lovac last:border-0 hover:bg-lovac-2/40">
                    <TableCell className="px-6 py-5 font-medium text-lovac-dark text-sm align-top">{r.k}</TableCell>
                    <TableCell className="px-6 py-5 text-sm text-lovac-muted align-top">
                      <div className="flex items-start gap-2">
                        <X size={16} className="text-lovac-muted shrink-0 mt-0.5" />
                        <span>{r.old}</span>
                      </div>
                    </TableCell>
                    <TableCell className="px-6 py-5 text-sm text-lovac-dark align-top">
                      <div className="flex items-start gap-2">
                        <Check size={16} className="text-lovac-accent shrink-0 mt-0.5" />
                        <span>{r.neu}</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </section>

      {/* 8. CTA FINALE */}
      <section className="section bg-lovac-dark text-on-dark grain relative overflow-hidden">
        <div className="container-narrow text-center relative z-10">
          <span className="badge-on-dark">Prenota una call</span>
          <h2 className="font-display text-4xl sm:text-5xl mt-6 text-on-dark leading-tight">
            Hai una posizione aperta<br />da troppo tempo?
          </h2>
          <p className="text-on-dark-muted mt-5 text-base max-w-xl mx-auto">
            Parlacene in una call di 30 minuti. Se non facciamo al caso tuo, te lo diremo.
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-10">
            <Link to="/contatti" className="btn-primary">
              Prenota una call <ArrowRight size={16} />
            </Link>
            <Link to="/hiring-scan" className="btn-outline-dark">
              Prova l'Hiring Scan <ArrowRight size={16} />
            </Link>
          </div>
          <p className="text-xs text-on-dark-muted mt-6 max-w-md mx-auto">
            Scopri quanto è difficile trovare la figura che cerchi.
          </p>
        </div>
      </section>
    </>
  );
}
